/**
 * HolderSwitcher — platform workspace picker.
 *
 * The single source of truth for "which workspace is the user
 * currently acting in." Selection persists in localStorage under one
 * platform-wide key (`fias.holder-switcher.selected`) and is mirrored
 * across every mounted hook instance in the same tab (custom event)
 * and across browser tabs (native `storage` event). Defaults to the
 * caller's Personal Holder on first load so authenticated users land
 * in a usable workspace immediately rather than facing a picker.
 *
 * Consumed by:
 *   - Global Header chip — always-visible current-workspace indicator
 *   - My Data, Vault Documents arche, Document Sign arche
 *   - Fias 360 (Personal + Org both supported)
 *   - Fias Leads (Personal + Org both supported)
 *
 * Per-arche hooks (`fias360/hooks/useSelectedHolder`,
 * `fias-leads/hooks/useSelectedHolder`) are thin compatibility
 * wrappers that delegate here.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Building2, User, Trash2 } from 'lucide-react';
import { api, type VaultMembershipDto, type VaultPersonalHolderDto } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from './ui/use-toast';
import { ToastAction } from './ui/toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const STORAGE_KEY = 'fias.holder-switcher.selected';
const SELECTION_CHANGE_EVENT = 'fias:holder-switcher:change';

const LEGACY_KEYS = ['fiasLeads.selectedHolder', 'fias360.selectedHolderId'] as const;

let didMigrateLegacyKeys = false;
function migrateLegacyKeysOnce(): void {
  if (didMigrateLegacyKeys || typeof window === 'undefined') return;
  didMigrateLegacyKeys = true;
  const ls = window.localStorage;
  if (!ls.getItem(STORAGE_KEY)) {
    for (const legacy of LEGACY_KEYS) {
      const v = ls.getItem(legacy);
      if (v) {
        ls.setItem(STORAGE_KEY, v);
        break;
      }
    }
  }
  for (const legacy of LEGACY_KEYS) ls.removeItem(legacy);
}

function readSelectedHolderId(): string | null {
  if (typeof window === 'undefined') return null;
  migrateLegacyKeysOnce();
  return window.localStorage.getItem(STORAGE_KEY);
}

function writeSelectedHolderId(next: string | null): void {
  if (typeof window === 'undefined') return;
  if (next) window.localStorage.setItem(STORAGE_KEY, next);
  else window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(SELECTION_CHANGE_EVENT));
}

export interface HolderSwitcherOption {
  holderId: string;
  holderType: 'personal' | 'org';
  displayName: string;
  /** 'self' for the personal entry; the explicit org role otherwise. */
  role: 'self' | 'owner' | 'admin' | 'member' | 'viewer';
}

export interface HolderSwitcherProps {
  /** Current selection (driven by the parent). Null = nothing
   * selected; the component shows a "select…" empty option. */
  selectedHolderId: string | null;
  onChange: (holderId: string | null, option: HolderSwitcherOption | null) => void;
  /** When true, the personal-holder entry is hidden. Useful for
   * surfaces (like Fias 360) that are org-only by product policy.
   * Default false. */
  hidePersonal?: boolean;
  /** When true, hide the "+ New" button. Read-only surfaces should
   * pass true; the My Data page surfaces the create flow elsewhere. */
  hideCreate?: boolean;
}

/**
 * Hook variant for callers that just want the resolved option list
 * + a persistent selection without rendering the dropdown UI.
 *
 * `defaultToPersonal` defaults to true — opt out only when a surface
 * needs an explicit "no holder picked" state (rare). Selection is
 * shared across every mounted hook instance in the same tab and
 * across browser tabs, so switching workspace anywhere updates
 * everywhere.
 */
export function useHolderSwitcher(opts?: { defaultToPersonal?: boolean }) {
  const defaultToPersonal = opts?.defaultToPersonal !== false;

  const query = useQuery({
    queryKey: queryKeys.vault.me.memberships(),
    queryFn: () => api.listMyVaultMemberships(),
  });

  const [selectedHolderId, setSelectedHolderIdState] = useState<string | null>(
    readSelectedHolderId,
  );

  // Sync this instance with selection changes from other hook
  // instances in the same tab (custom event) and other tabs (native
  // storage event).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const sync = () => setSelectedHolderIdState(readSelectedHolderId());
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY || e.key === null) sync();
    };
    window.addEventListener(SELECTION_CHANGE_EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SELECTION_CHANGE_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setSelectedHolderId = (next: string | null) => {
    setSelectedHolderIdState(next);
    writeSelectedHolderId(next);
  };

  // Default to personal on first load when nothing's saved.
  useEffect(() => {
    if (!defaultToPersonal) return;
    if (selectedHolderId) return;
    const personal = query.data?.personalHolder?.holderId;
    if (personal) setSelectedHolderId(personal);
  }, [query.data?.personalHolder?.holderId, defaultToPersonal, selectedHolderId]);

  // Auto-clear when the selected holder is no longer accessible
  // (revoked membership, etc.). Skip while a refetch is in flight —
  // the in-memory list is known to be about to change, and checking
  // against it would clear a freshly-created workspace before its
  // membership row lands. Once the refetch completes the effect
  // re-runs against the new data.
  useEffect(() => {
    if (!selectedHolderId || !query.data || query.isFetching) return;
    const stillValid =
      query.data.personalHolder?.holderId === selectedHolderId ||
      query.data.memberships.some((m) => m.holderId === selectedHolderId && m.status === 'active');
    if (!stillValid) setSelectedHolderId(null);
  }, [selectedHolderId, query.data, query.isFetching]);

  const options: HolderSwitcherOption[] = [];
  if (query.data?.personalHolder) {
    options.push({
      holderId: query.data.personalHolder.holderId,
      holderType: 'personal',
      displayName: query.data.personalHolder.displayName,
      role: 'self',
    });
  }
  for (const m of query.data?.memberships ?? []) {
    if (m.status !== 'active' || m.holderType !== 'org') continue;
    options.push({
      holderId: m.holderId,
      holderType: 'org',
      displayName: m.holderDisplayName,
      role: m.role,
    });
  }

  const selectedOption = options.find((o) => o.holderId === selectedHolderId) ?? null;

  return {
    options,
    selectedHolderId,
    selectedOption,
    setSelectedHolderId,
    isLoading: query.isLoading,
    isError: query.isError,
    /** The caller's own Principal ID — used by team-management UI
     * to flag "you" without exposing other members' principals. */
    principalId: query.data?.principalId ?? null,
    /** Direct membership data for callers that need more than the
     * synthesized option list — e.g. the team-management tab. */
    memberships: (query.data?.memberships ?? []) as VaultMembershipDto[],
    personalHolder: (query.data?.personalHolder ?? null) as VaultPersonalHolderDto | null,
  };
}

/**
 * Soft-delete an org workspace and surface an Undo affordance.
 *
 * Two cache touches on success:
 *   1. Invalidate the memberships list so the deleted workspace
 *      disappears from every switcher instance.
 *   2. Drop the selection back to Personal if the deleted workspace
 *      was the active one — otherwise the deleted ID lingers in
 *      localStorage and the autoclear would set it to null on next
 *      mount (visible flash, same shape as the create-race bug).
 *
 * Undo is wired via the toast's action button; calling undelete
 * during the grace window restores the Holder. After grace, the
 * purger (TODO follow-up) hard-deletes and undelete returns 404.
 */
function useDeleteOrgHolderMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (holderId: string) => api.deleteVaultOrgHolder(holderId),
    onSuccess: async (data, holderId) => {
      const currentSelection = readSelectedHolderId();
      if (currentSelection === holderId) writeSelectedHolderId(null);
      await qc.invalidateQueries({ queryKey: queryKeys.vault.me.memberships() });
      toast({
        title: 'Workspace deleted',
        description: `Permanent deletion in ${data.graceDays} days. You can undo until then.`,
        action: (
          <ToastAction
            altText="Undo workspace deletion"
            onClick={async () => {
              try {
                await api.undeleteVaultOrgHolder(holderId);
                await qc.invalidateQueries({ queryKey: queryKeys.vault.me.memberships() });
                writeSelectedHolderId(holderId);
                toast({ title: 'Workspace restored' });
              } catch (err) {
                toast({
                  title: 'Restore failed',
                  description: err instanceof Error ? err.message : 'Unknown error',
                  variant: 'destructive',
                });
              }
            }}
          >
            Undo
          </ToastAction>
        ),
      });
    },
    onError: (err) => {
      toast({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    },
  });
}

interface DeleteWorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceName: string;
  onConfirm: () => void;
  isPending: boolean;
}

function DeleteWorkspaceDialog({
  open,
  onOpenChange,
  workspaceName,
  onConfirm,
  isPending,
}: DeleteWorkspaceDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete &ldquo;{workspaceName}&rdquo;?</AlertDialogTitle>
          <AlertDialogDescription>
            This workspace and all of its data will be hidden immediately and permanently deleted
            after 30 days. You can undo from the toast or by restoring before the grace window ends.
            Everyone with access to this workspace will lose it.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending ? 'Deleting…' : 'Delete workspace'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function HolderSwitcher({
  selectedHolderId,
  onChange,
  hidePersonal,
  hideCreate,
}: HolderSwitcherProps) {
  const switcher = useHolderSwitcher();
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const qc = useQueryClient();

  const createOrg = useMutation({
    mutationFn: (displayName: string) => api.createVaultOrgHolder(displayName),
    onSuccess: async (data) => {
      // Await the refetch before broadcasting the new selection.
      // Otherwise the auto-clear effect in useHolderSwitcher races
      // against the still-stale membership list and clears the
      // selection back to null — under real RTT this produces a
      // visible flash to the empty state and drops the user back to
      // Personal. See useHolderSwitcher autoclear comment.
      await qc.invalidateQueries({ queryKey: queryKeys.vault.me.memberships() });
      const option: HolderSwitcherOption = {
        holderId: data.holder.holderId,
        holderType: 'org',
        displayName: data.holder.displayName,
        role: 'owner',
      };
      onChange(data.holder.holderId, option);
      setShowCreate(false);
      setName('');
    },
  });

  if (switcher.isLoading) {
    return <span className="text-sm text-muted-foreground">Loading workspaces…</span>;
  }

  const options = hidePersonal
    ? switcher.options.filter((o) => o.holderType !== 'personal')
    : switcher.options;

  if (showCreate || (options.length === 0 && !hideCreate)) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = name.trim();
          if (!trimmed) return;
          createOrg.mutate(trimmed);
        }}
        className="flex items-center gap-2"
      >
        <input
          type="text"
          placeholder="Organization name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          className="rounded border border-border bg-card px-2 py-1 text-sm"
          autoFocus
        />
        <button
          type="submit"
          disabled={!name.trim() || createOrg.isPending}
          className="rounded bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50"
        >
          {createOrg.isPending ? 'Creating…' : 'Create'}
        </button>
        {options.length > 0 && (
          <button
            type="button"
            className="text-sm text-muted-foreground underline"
            onClick={() => setShowCreate(false)}
          >
            Cancel
          </button>
        )}
        {createOrg.isError && (
          <span className="text-xs text-destructive">{(createOrg.error as Error).message}</span>
        )}
      </form>
    );
  }

  // When the only option is the user's Personal Holder there's nothing to
  // switch between — drop the dropdown but keep "+ New" so the user still
  // has an entry point to create their first Org workspace.
  const personalOnly = options.length === 1 && options[0]?.holderType === 'personal';

  return (
    <div className="flex items-center gap-3">
      {!personalOnly && (
        <label className="text-sm text-muted-foreground">
          Workspace:
          <select
            value={selectedHolderId ?? ''}
            onChange={(e) => {
              const next = e.target.value || null;
              const option = next ? (options.find((o) => o.holderId === next) ?? null) : null;
              onChange(next, option);
            }}
            className="ml-2 rounded border border-border bg-card px-2 py-1 text-sm"
          >
            <option value="">— select —</option>
            {options.map((o) => (
              <option key={o.holderId} value={o.holderId}>
                {o.displayName}
                {o.role !== 'self' ? ` (${o.role})` : ''}
              </option>
            ))}
          </select>
        </label>
      )}
      {!hideCreate && (
        <button
          type="button"
          className="text-sm text-muted-foreground underline"
          onClick={() => setShowCreate(true)}
        >
          + New workspace
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HolderSwitcherChip — compact Header indicator + dropdown switcher.
//
// Always-visible "you are here" pill so users notice when they're
// operating in the wrong workspace. Read-mostly: the dropdown lets
// you switch, but Org creation lives in the arche-level <HolderSwitcher>.
// ---------------------------------------------------------------------------

interface HolderSwitcherChipProps {
  /** Compact layout for mobile / tight headers (hides label, narrower). */
  compact?: boolean;
  /** Called after the user selects a workspace; useful for closing a
   * containing mobile menu sheet. */
  onAfterSelect?: () => void;
}

export function HolderSwitcherChip({ compact = false, onAfterSelect }: HolderSwitcherChipProps) {
  const { options, selectedOption, setSelectedHolderId, isLoading } = useHolderSwitcher();
  const deleteMutation = useDeleteOrgHolderMutation();
  const [confirmingDeleteFor, setConfirmingDeleteFor] = useState<HolderSwitcherOption | null>(null);

  if (isLoading && options.length === 0) {
    return <span className="text-xs text-muted-foreground px-2 py-1">Workspace…</span>;
  }

  // No options at all (rare: auth race, network failure). Don't render
  // a broken trigger — the user has no workspace to switch to anyway.
  if (options.length === 0) return null;

  // Only the personal workspace — nothing to switch between, so hide the chip.
  if (options.length === 1 && options[0]?.holderType === 'personal') return null;

  const label = selectedOption?.displayName ?? 'Choose workspace';
  const Icon = selectedOption?.holderType === 'org' ? Building2 : User;

  // Delete is owner-only and applies only to the currently selected
  // org workspace. Surfacing it on every row inflates the dropdown
  // and tempts mis-clicks; pinning it to "the workspace you're acting
  // in right now" keeps the destructive affordance behind the
  // selection gesture.
  const canDeleteCurrent = selectedOption?.holderType === 'org' && selectedOption.role === 'owner';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Active workspace: ${label}. Click to switch.`}
            className={
              'flex items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-sm hover:bg-accent transition-colors ' +
              (compact ? 'max-w-[10rem]' : 'max-w-[16rem]')
            }
          >
            <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate text-foreground">{label}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Workspace</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {options.map((o) => {
            const isActive = o.holderId === selectedOption?.holderId;
            const RowIcon = o.holderType === 'org' ? Building2 : User;
            return (
              <DropdownMenuItem
                key={o.holderId}
                onSelect={() => {
                  setSelectedHolderId(o.holderId);
                  onAfterSelect?.();
                }}
                className="flex items-center gap-2"
              >
                <RowIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="flex-1 truncate">{o.displayName}</span>
                {o.role !== 'self' && (
                  <span className="text-xs text-muted-foreground capitalize">{o.role}</span>
                )}
                {isActive && <Check className="h-4 w-4 text-primary shrink-0" />}
              </DropdownMenuItem>
            );
          })}
          {canDeleteCurrent && selectedOption && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  setConfirmingDeleteFor(selectedOption);
                }}
                className="flex items-center gap-2 text-destructive focus:text-destructive focus:bg-destructive/10"
              >
                <Trash2 className="h-4 w-4 shrink-0" />
                <span className="flex-1 truncate">
                  Delete &ldquo;{selectedOption.displayName}&rdquo;
                </span>
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <DeleteWorkspaceDialog
        open={confirmingDeleteFor !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmingDeleteFor(null);
        }}
        workspaceName={confirmingDeleteFor?.displayName ?? ''}
        isPending={deleteMutation.isPending}
        onConfirm={() => {
          if (!confirmingDeleteFor) return;
          deleteMutation.mutate(confirmingDeleteFor.holderId, {
            onSettled: () => setConfirmingDeleteFor(null),
          });
        }}
      />
    </>
  );
}
